<template>
  <div>
    <b-card
      v-if="$can('view-service-type')"
      no-body
    >
      <div class="service-type-toolbar">
        <b-button
          v-if="$can('add-service-type')"
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          class="custom-button-color service-type-add"
          @click="openAddModal"
        >
          + Add New
        </b-button>
        <ReportTableButtons
          class="service-type-toolbar-actions"
          :items="items"
          :json_fieldz="json_fields"
          :name="`Service Types - Page ${page}.xls`"
          :bulkname="`Service Types.xls`"
          :fetch="getServiceTypesNoPagination"
          @refresh="onClickRefresh"
        />
      </div>
      <b-overlay
        variant="light"
        opacity="0.30"
        blur="10px"
        rounded="sm"
      >
        <b-table
          :per-page="0"
          :fields="fields"
          :items="items"
          hover
          responsive
          striped
          show-empty
          class="hide-vertical-scroll"
        >
          <template #empty>
            <TableDataFetching
              :rows="items"
              :data-loading="dataLoading"
            />
          </template>
          <template #cell(name)="data">
            {{ data.item.name }}
          </template>
          <template #cell(linked_rate_cards)="data">
            <div v-if="data.item.linked_rate_cards && data.item.linked_rate_cards.total">
              <a
                href="javascript:void(0)"
                @click="openLinkedRateCards(data.item)"
              >
                {{ data.item.linked_rate_cards.preview }}
                <span v-if="data.item.linked_rate_cards.extra_count">
                  +{{ data.item.linked_rate_cards.extra_count }} more
                </span>
              </a>
            </div>
            <span v-else>N/A</span>
          </template>
          <template #cell(is_active)="data">
            <b-badge
              v-if="isActive(data.item.is_active)"
              variant="light-success"
            >
              Active
            </b-badge>
            <b-badge
              v-else
              variant="light-danger"
            >
              Inactive
            </b-badge>
          </template>
          <template #cell(action)="data">
            <b-dropdown
              v-if="$can('inactive-service-type')"
              variant="link"
              toggle-class="text-decoration-none"
              :class="items.length !== 1 ? 'dropdown-no-scroll' : ''"
              no-caret
              right
              boundary="viewport"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="18"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item
                v-if="isActive(data.item.is_active)"
                @click="changeStatus(data.item, false)"
              >
                <feather-icon icon="ToolIcon" />
                Inactive
              </b-dropdown-item>
              <b-dropdown-item
                v-else
                @click="changeStatus(data.item, true)"
              >
                <feather-icon icon="ToolIcon" />
                Active
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-overlay>
      <div class="mx-2 mb-2">
        <b-row cols="12">
          <b-col
            cols="auto"
            class="d-flex align-items-center justify-content-sm-start"
          >
            <label class="mb-0">Per page</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="pageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-1"
            />
          </b-col>
          <b-col
            class="d-flex align-items-center justify-content-end"
          >
            <span class="text-muted mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
            <b-pagination
              v-model="page"
              :per-page="perPage"
              :total-rows="meta.total ? meta.total : 0"
              class="mb-0 mt-1 mt-sm-0"
              first-number
              last-number
              next-class="next-item"
              prev-class="prev-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <b-modal
      id="add-service-type-modal"
      v-model="addModalOpen"
      title="ADD SERVICE TYPE"
      centered
      hide-footer
      no-close-on-backdrop
    >
      <b-overlay
        :show="addLoading"
        blur="10px"
        opacity="0.30"
        rounded="sm"
        variant="light"
      >
        <b-form @submit.prevent="submitServiceType">
          <b-form-group
            class="required"
            label="Service Type"
            label-for="service-type-name"
          >
            <b-form-input
              id="service-type-name"
              v-model="addForm.name"
              placeholder="Enter Service Type"
              required
              maxlength="100"
              autocomplete="nope"
            />
          </b-form-group>
          <b-form-group>
            <b-form-checkbox
              v-model="addForm.is_active"
              :value="true"
              :unchecked-value="false"
            >
              Active
            </b-form-checkbox>
          </b-form-group>
          <div class="d-flex justify-content-end mt-2">
            <b-button
              type="submit"
              variant="primary"
              class="mr-1 custom-button-color"
            >
              Submit
            </b-button>
            <b-button
              type="button"
              variant="outline-secondary"
              @click="clearAddForm"
            >
              Clear
            </b-button>
          </div>
        </b-form>
      </b-overlay>
    </b-modal>

    <b-modal
      id="linked-rate-cards-modal"
      v-model="linkedModalOpen"
      title="Linked Rate Cards"
      size="xl"
      centered
      hide-footer
    >
      <b-overlay
        blur="10px"
        opacity="0.30"
        rounded="sm"
        variant="light"
      >
        <b-table
          :fields="linkedFields"
          :items="linkedItems"
          hover
          responsive
          show-empty
        >
          <template #empty>
            <TableDataFetching
              :rows="linkedItems"
              :data-loading="linkedLoading"
            />
          </template>
          <template #cell(card_name)="data">
            <router-link
              v-if="data.item.id"
              class="linked-rate-card-name"
              :to="{ name: 'view-rate-card', params: { rateCardType: 'service', id: data.item.id } }"
              @click.native="linkedModalOpen = false"
            >
              {{ data.item.card_name }}
            </router-link>
            <span v-else>{{ data.item.card_name }}</span>
          </template>
          <template #cell(created_at)="data">
            {{ __dateTimeFormatter(data.item.created_at) || '---' }}
          </template>
          <template #cell(modified_by)="data">
            <div v-if="data.item.modified_by">
              <div>{{ data.item.modified_by.name || '---' }}</div>
              <small class="text-muted">{{ data.item.modified_by.email }}</small>
            </div>
            <span v-else>—</span>
          </template>
          <template #cell(assigned_merchants)="data">
            {{ Number(data.item.assigned_merchants || 0).toLocaleString() }}
          </template>
        </b-table>
        <div class="mt-1">
          <b-row>
            <b-col
              cols="auto"
              class="d-flex align-items-center"
            >
              <label class="mb-0">Per page</label>
              <v-select
                v-model="linkedPerPage"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block ml-1"
              />
            </b-col>
            <b-col
              class="d-flex align-items-center justify-content-end"
            >
              <span class="text-muted mr-1">Showing {{ linkedMeta.from }} to {{ linkedMeta.to }} of {{ linkedMeta.total }} entries</span>
              <b-pagination
                v-model="linkedPage"
                :per-page="linkedPerPage"
                :total-rows="linkedMeta.total ? linkedMeta.total : 0"
                class="mb-0"
                first-number
                last-number
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BOverlay,
  BTable,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

const ServiceTypeRepository = RepositoryFactory.get('serviceType')

export default {
  name: 'ServiceTypeList',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BOverlay,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    vSelect,
  },
  directives: {
    Ripple,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      dataLoading: false,
      addLoading: false,
      linkedLoading: false,
      addModalOpen: false,
      linkedModalOpen: false,
      selectedServiceTypeId: null,
      items: [],
      meta: {},
      perPage: 20,
      page: 1,
      pageOptions: [10, 20, 50],
      addForm: {
        name: '',
        is_active: false,
      },
      linkedItems: [],
      linkedMeta: {},
      linkedPage: 1,
      linkedPerPage: 20,
      fields: [
        {
          key: 'name',
          label: 'SERVICE TYPE',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'linked_rate_cards',
          label: 'LINKED RATE CARDS',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'is_active',
          label: 'STATUS',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'action',
          label: 'ACTION',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
      ],
      linkedFields: [
        {
          key: 'card_name',
          label: 'RATE CARD NAME',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'created_at',
          label: 'CREATED DATE',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'modified_by',
          label: 'MODIFIED BY',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'assigned_merchants',
          label: 'ASSIGNED MERCHANTS',
          thClass: 'text-right',
          tdClass: 'text-right',
        },
      ],
      json_fields: {
        'Service Type': 'name',
        'Linked Rate Cards': {
          callback: value => {
            const linked = value.linked_rate_cards
            if (!linked || !linked.total) return 'N/A'
            if (linked.extra_count) {
              return `${linked.preview} +${linked.extra_count} more`
            }
            return linked.preview || 'N/A'
          },
        },
        Status: {
          field: 'is_active',
          callback: value => (value === true || value === 1 ? 'Active' : 'Inactive'),
        },
      },
    }
  },
  watch: {
    page() {
      this.fetchServiceTypes()
    },
    perPage() {
      this.page = 1
      this.fetchServiceTypes()
    },
    linkedPage() {
      if (this.linkedModalOpen && this.selectedServiceTypeId) {
        this.fetchLinkedRateCards()
      }
    },
    linkedPerPage() {
      this.linkedPage = 1
      if (this.linkedModalOpen && this.selectedServiceTypeId) {
        this.fetchLinkedRateCards()
      }
    },
  },
  mounted() {
    this.fetchServiceTypes()
  },
  methods: {
    isActive(value) {
      return value === true || value === 1 || value === '1'
    },
    async fetchServiceTypes() {
      this.dataLoading = true
      this.items = []
      try {
        const { data } = await ServiceTypeRepository.getServiceTypes(this.page, this.perPage)
        this.items = data.data
        this.meta = data.meta || {}
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async getServiceTypesNoPagination() {
      try {
        const { data } = await ServiceTypeRepository.getServiceTypesNoPagination()
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    openAddModal() {
      this.clearAddForm()
      this.addModalOpen = true
    },
    clearAddForm() {
      this.addForm = {
        name: '',
        is_active: false,
      }
    },
    async submitServiceType() {
      this.addLoading = true
      try {
        const res = await ServiceTypeRepository.createServiceType({
          name: this.addForm.name,
          is_active: !!this.addForm.is_active,
        })
        if (res.status === 200) {
          this.showSuccessMessage('Service Type created successfully')
          this.addModalOpen = false
          this.clearAddForm()
          this.fetchServiceTypes()
        } else {
          this.showErrorMessage('Cannot create Service Type')
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.addLoading = false
    },
    async changeStatus(item, isActive) {
      try {
        const res = await ServiceTypeRepository.changeStatus(item.id, isActive)
        if (res.status === 200) {
          this.showSuccessMessage(res.data.message || 'Service Type status updated successfully')
          this.fetchServiceTypes()
        } else {
          this.showErrorMessage('Cannot update Service Type status')
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    openLinkedRateCards(item) {
      this.selectedServiceTypeId = item.id
      this.linkedPage = 1
      this.linkedModalOpen = true
      this.fetchLinkedRateCards()
    },
    async fetchLinkedRateCards() {
      this.linkedLoading = true
      this.linkedItems = []
      try {
        const { data } = await ServiceTypeRepository.getLinkedRateCards(
          this.selectedServiceTypeId,
          this.linkedPage,
          this.linkedPerPage,
        )
        this.linkedItems = data.data
        this.linkedMeta = data.meta || {}
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.linkedLoading = false
    },
    onClickRefresh() {
      this.fetchServiceTypes()
    },
  },
}
</script>

<style scoped>
.required ::v-deep label::after {
  content: '*';
  color: #ea5455;
}
.custom-button-color {
  background-color: #4186f5 !important;
  border-color: #4186f5 !important;
}
.service-type-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 0.5rem;
  padding: 0.75rem 2rem;
}
.service-type-add,
.service-type-toolbar-actions {
  flex-shrink: 0;
}
.service-type-toolbar-actions {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  margin-left: auto;
  white-space: nowrap;
}
.per-page-selector {
  width: 110px;
  flex-shrink: 0;
}
.linked-rate-card-name {
  color: #049cfb;
  text-decoration: none;
}
.linked-rate-card-name:hover,
.linked-rate-card-name:focus {
  color: #049cfb;
  text-decoration: none;
}
</style>
